import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider } from "@/context/AuthContext";
import { Toaster } from "sonner";

import Home from "@/pages/public/Home";
import Pricing from "@/pages/public/Pricing";
import StaticPage from "@/pages/public/StaticPage";
import Login from "@/pages/auth/Login";
import Register from "@/pages/auth/Register";
import ForgotPassword from "@/pages/auth/ForgotPassword";
import { TeluguMatrimony, IndianMatrimony, LifePartner, MatrimonyGuide, Blog } from "@/pages/public/SeoPages";
import AdminLogin from "@/pages/auth/AdminLogin";
import AdminActivate from "@/pages/auth/AdminActivate";
import AuthCallback from "@/pages/auth/AuthCallback";

import { AppLayout, ProtectedRoute } from "@/components/AppLayout";
import Dashboard from "@/pages/app/Dashboard";
import Matches from "@/pages/app/Matches";
import Search from "@/pages/app/Search";
import ProfileView from "@/pages/app/ProfileView";
import EditProfile from "@/pages/app/EditProfile";
import MyProfile from "@/pages/app/MyProfile";
import Preferences from "@/pages/app/Preferences";
import Interests from "@/pages/app/Interests";
import Shortlist from "@/pages/app/Shortlist";
import Messages from "@/pages/app/Messages";
import Notifications from "@/pages/app/Notifications";
import Requests from "@/pages/app/Requests";
import CompatibilityLab from "@/pages/app/CompatibilityLab";
import Verification from "@/pages/app/Verification";
import Settings from "@/pages/app/Settings";
import Subscription from "@/pages/app/Subscription";
import Family from "@/pages/app/Family";
import SafetyCenter from "@/pages/app/SafetyCenter";
import Discovery from "@/pages/app/Discovery";
import Referral from "@/pages/app/Referral";
import Boost from "@/pages/app/Boost";
import Events from "@/pages/app/Events";
import MatchAssist from "@/pages/app/MatchAssist";
import Broadcast from "@/pages/app/Broadcast";
import Calls from "@/pages/app/Calls";
import DecisionWorkspace from "@/pages/app/DecisionWorkspace";
import Understanding from "@/pages/app/Understanding";

import { AdminLayout, AdminRoute } from "@/pages/admin/AdminLayout";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminCommandCenter from "@/pages/admin/AdminCommandCenter";
import AdminPlatformControl from "@/pages/admin/AdminPlatformControl";
import AdminFeatureWorkspace from "@/pages/admin/AdminFeatureWorkspace";
import AdminUsers from "@/pages/admin/AdminUsers";
import AdminUser360 from "@/pages/admin/AdminUser360";
import AdminModeration from "@/pages/admin/AdminModeration";
import AdminTeam from "@/pages/admin/AdminTeam";
import AdminFeatureFlags from "@/pages/admin/AdminFeatureFlags";
import AdminAnalytics from "@/pages/admin/AdminAnalytics";
import AdminIntelligence from "@/pages/admin/AdminIntelligence";
import AdminSafety from "@/pages/admin/AdminSafety";
import { AdminVerifications, AdminReports, AdminSubscriptions, AdminSupport, AdminEvents, AdminMatchAssist, AdminCoupons } from "@/pages/admin/AdminPages";

function GoogleCallbackGate({ children }) {
  const loc = useLocation();
  if ((loc.hash || "").includes("session_id=")) return <AuthCallback />;
  return children;
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Toaster position="top-center" richColors />
        <GoogleCallbackGate>
          <Routes>
            {/* Public */}
            <Route path="/" element={<Home />} />
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/telugu-matrimony" element={<TeluguMatrimony />} />
            <Route path="/indian-matrimony" element={<IndianMatrimony />} />
            <Route path="/life-partner" element={<LifePartner />} />
            <Route path="/matrimony-guide" element={<MatrimonyGuide />} />
            <Route path="/blog" element={<Blog />} />
            <Route path="/about" element={<StaticPage slugOverride="about" />} />
            <Route path="/how-it-works" element={<StaticPage slugOverride="how-it-works" />} />
            <Route path="/safety" element={<StaticPage slugOverride="safety" />} />
            <Route path="/faq" element={<StaticPage slugOverride="faq" />} />
            <Route path="/contact" element={<StaticPage slugOverride="contact" />} />
            <Route path="/legal/:slug" element={<StaticPage />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/auth/callback" element={<AuthCallback />} />
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin/activate" element={<AdminActivate />} />

            {/* App */}
            <Route path="/app" element={<ProtectedRoute><AppLayout /></ProtectedRoute>}>
              <Route index element={<Navigate to="/app/dashboard" replace />} />
              <Route path="dashboard" element={<Dashboard />} />
              <Route path="matches" element={<Matches />} />
              <Route path="matches/:category" element={<Matches />} />
              <Route path="search" element={<Search />} />
              <Route path="profile" element={<MyProfile />} />
              <Route path="profile/edit" element={<EditProfile />} />
              <Route path="profile/:userId" element={<ProfileView />} />
              <Route path="preferences" element={<Preferences />} />
              <Route path="interests" element={<Interests />} />
              <Route path="shortlist" element={<Shortlist />} />
              <Route path="jodi/decision-workspace" element={<DecisionWorkspace />} />
              <Route path="jodi/understanding" element={<Understanding />} />
              <Route path="jodi/understanding/:target" element={<Understanding />} />
              <Route path="messages" element={<Messages />} />
              <Route path="messages/:otherId" element={<Messages />} />
              <Route path="notifications" element={<Notifications />} />
              <Route path="requests" element={<Requests />} />
              <Route path="compatibility-lab" element={<CompatibilityLab />} />
              <Route path="verification" element={<Verification />} />
              <Route path="settings" element={<Settings />} />
              <Route path="subscription" element={<Subscription />} />
              <Route path="family" element={<Family />} />
              <Route path="safety" element={<SafetyCenter />} />
              <Route path="discover" element={<Discovery />} />
              <Route path="discover/:mode" element={<Discovery />} />
              <Route path="referrals" element={<Referral />} />
              <Route path="boost" element={<Boost />} />
              <Route path="events" element={<Events />} />
              <Route path="match-assist" element={<MatchAssist />} />
              <Route path="broadcast" element={<Broadcast />} />
              <Route path="calls" element={<Calls />} />
            </Route>

            {/* Admin */}
            <Route path="/admin" element={<AdminRoute><AdminLayout /></AdminRoute>}>
              <Route index element={<Navigate to="/admin/command-center" replace />} />
              <Route path="command-center" element={<AdminCommandCenter />} />
              <Route path="platform-control" element={<AdminPlatformControl />} />
              <Route path="f/:key" element={<AdminFeatureWorkspace />} />
              <Route path="dashboard" element={<AdminDashboard />} />
              <Route path="users" element={<AdminUsers />} />
              <Route path="user/:uid" element={<AdminUser360 />} />
              <Route path="moderation" element={<AdminModeration />} />
              <Route path="team" element={<AdminTeam />} />
              <Route path="feature-flags" element={<AdminFeatureFlags />} />
              <Route path="analytics" element={<AdminAnalytics />} />
              <Route path="intelligence" element={<AdminIntelligence />} />
              <Route path="safety" element={<AdminSafety />} />
              <Route path="verifications" element={<AdminVerifications />} />
              <Route path="reports" element={<AdminReports />} />
              <Route path="subscriptions" element={<AdminSubscriptions />} />
              <Route path="support" element={<AdminSupport />} />
              <Route path="events" element={<AdminEvents />} />
              <Route path="match-assist" element={<AdminMatchAssist />} />
              <Route path="coupons" element={<AdminCoupons />} />
            </Route>

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </GoogleCallbackGate>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
